import { useState, useMemo } from 'react'
import {
  Box, Typography, Button, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, Card, Stack, InputAdornment,
} from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  Add, Edit, Delete, Block, Business, CheckCircle,
  Subscriptions, Search, Close,
} from '@mui/icons-material'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import {
  useAdminTenants, useAdminCreateTenant, useAdminUpdateTenant,
  useAdminSuspendTenant, useAdminDeleteTenant,
  useAdminActivateTenant,
  useBootstrapTenantAdmin,
  useAdminSubscriptions, useAdminSubscribeTenant,
  usePricingTiers,
  type AdminSubscription,
} from '../../hooks/usePlatformAdmin'
import { adminGridSx } from '../../layouts/AdminLayout'
import { format } from 'date-fns'
import { isPasswordValid, passwordError } from '../../lib/password'
import { AdminDataError } from '../../components/AdminDataError'
import { AdminPageHeader } from '../../components/AdminPageHeader'
import { AdminEmptyState } from '../../components/AdminEmptyState'

const STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  active:      { bg: '#f0fdf4', color: '#10b981', border: '#bbf7d0' },
  suspended:   { bg: '#fff7ed', color: '#f97316', border: '#fed7aa' },
  trial:       { bg: '#eff6ff', color: '#3b82f6', border: '#bfdbfe' },
  unknown:     { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
}

const SUB_STATUS_COLORS: Record<string, { bg: string; color: string; border: string }> = {
  active:    { bg: '#f0fdf4', color: '#10b981', border: '#bbf7d0' },
  grace:     { bg: '#fffbeb', color: '#f59e0b', border: '#fde68a' },
  expired:   { bg: '#fff1f2', color: '#ef4444', border: '#fecaca' },
  cancelled: { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
}

const CURRENCIES = ['USD', 'EUR', 'IQD']
const DEFAULT_TENANT_APP_URL = ['localhost', '127.0.0.1'].includes(window.location.hostname)
  ? `${window.location.protocol}//${window.location.hostname}:3001`
  : 'https://omnitrace.app'
const TENANT_LOGIN_URL = `${(import.meta.env.VITE_TENANT_APP_URL || DEFAULT_TENANT_APP_URL).replace(/\/$/, '')}/auth/login`

type Tenant = { id: string; name: string; status: string; currency: string; created_at: string }
const emptyForm = {
  name: '', currency: 'USD',
  admin_name: '', admin_email: '', admin_password: '',
}

function getDaysRemaining(expiresAt: string): number {
  const diff = new Date(expiresAt).getTime() - Date.now()
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)))
}

export default function AdminTenantsPage() {
  const tenantsQuery = useAdminTenants()
  const subscriptionsQuery = useAdminSubscriptions()
  const tiersQuery = usePricingTiers()
  const { data: tenants = [], isLoading } = tenantsQuery
  const { data: subscriptions = [] } = subscriptionsQuery
  const { data: tiers = [] } = tiersQuery
  const createTenant     = useAdminCreateTenant()
  const updateTenant     = useAdminUpdateTenant()
  const suspendTenant    = useAdminSuspendTenant()
  const activateTenant   = useAdminActivateTenant()
  const deleteTenant     = useAdminDeleteTenant()
  const bootstrapAdmin   = useBootstrapTenantAdmin()
  const subscribeTenant  = useAdminSubscribeTenant()

  const [createdInfo, setCreatedInfo] = useState<{ tenant: string; email: string } | null>(null)

  const [dialogOpen, setDialogOpen]             = useState(false)
  const [editing, setEditing]                   = useState<Tenant | null>(null)
  const [form, setForm]                         = useState(emptyForm)
  const [deleteTarget, setDeleteTarget]         = useState<string | null>(null)
  const [suspendTarget, setSuspendTarget]       = useState<string | null>(null)
  const [search, setSearch]                     = useState('')
  const [statusFilter, setStatusFilter]         = useState<'all' | 'active' | 'suspended'>('all')

  // Subscribe dialog
  const [subDialogOpen, setSubDialogOpen] = useState(false)
  const [subTenantId, setSubTenantId]     = useState('')
  const [subForm, setSubForm]             = useState({ device_count: '5', billing_cycle: 'monthly', amount: '', description: '' })

  // Index subscriptions by tenant_id for fast lookup
  const subsByTenant = useMemo(() => {
    const map = new Map<string, AdminSubscription>()
    for (const s of subscriptions) map.set(s.tenant_id, s)
    return map
  }, [subscriptions])

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true) }
  const openEdit   = (t: Tenant) => {
    setEditing(t)
    setForm({
      name: t.name, currency: t.currency || 'USD',
      admin_name: '', admin_email: '', admin_password: '',
    })
    setDialogOpen(true)
  }

  const openSubscribe = (tenantId: string) => {
    setSubTenantId(tenantId)
    setSubForm({ device_count: '5', billing_cycle: 'monthly', amount: '', description: '' })
    setSubDialogOpen(true)
  }

  const handleSubmit = async () => {
    try {
      if (editing) {
        await updateTenant.mutateAsync({ id: editing.id, name: form.name.trim() })
        setDialogOpen(false)
        return
      }
      const tenant = await createTenant.mutateAsync({
        name: form.name.trim(),
        currency: form.currency,
      })
      try {
        await bootstrapAdmin.mutateAsync({
          tenant_id: tenant.id,
          name: form.admin_name.trim() || form.admin_email.trim(),
          email: form.admin_email.trim(),
          password: form.admin_password,
        })
      } catch (error) {
        // The UI promises a tenant + initial admin as one operation. Compensate
        // if the second service rejects the admin so retries do not create orphans.
        try { await deleteTenant.mutateAsync(tenant.id) } catch { /* original error remains visible */ }
        throw error
      }
      setCreatedInfo({ tenant: form.name.trim(), email: form.admin_email.trim() })
      setDialogOpen(false)
    } catch { /* API interceptor shows the failure */ }
  }

  const handleSubscribe = async () => {
    try {
      await subscribeTenant.mutateAsync({
        tenant_id: subTenantId,
        device_count: parseInt(subForm.device_count),
        billing_cycle: subForm.billing_cycle,
        amount: subForm.amount ? parseFloat(subForm.amount) : undefined,
        description: subForm.description || undefined,
      })
      setSubDialogOpen(false)
    } catch { /* API interceptor shows the failure */ }
  }

  const activeCt = tenants.filter((t: Tenant) => t.status === 'active').length
  const filteredTenants = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return (tenants as Tenant[]).filter((tenant) => {
      const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter
      const matchesSearch = !needle || tenant.name.toLowerCase().includes(needle) ||
        tenant.id.toLowerCase().includes(needle) || tenant.currency?.toLowerCase().includes(needle)
      return matchesStatus && matchesSearch
    })
  }, [tenants, search, statusFilter])

  const columns: GridColDef[] = [
    {
      field: 'name', headerName: 'Tenant', flex: 1, minWidth: 180,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: 1,
            bgcolor: '#EBF6FD', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Business sx={{ fontSize: 15, color: '#34A8E0' }} />
          </Box>
          <Typography variant="body2" color="#1e293b" fontWeight={500}>{row.name}</Typography>
        </Box>
      ),
    },
    {
      field: 'subscription', headerName: 'Subscription', width: 220, sortable: false,
      renderCell: ({ row }) => {
        const sub = subsByTenant.get(row.id)
        if (!sub) {
          return (
            <Chip label="No subscription" size="small" sx={{
              bgcolor: '#f8fafc', color: '#94a3b8', border: '1px solid #e2e8f0',
              fontSize: 11, fontWeight: 500,
            }} />
          )
        }
        const sc = SUB_STATUS_COLORS[sub.status] ?? SUB_STATUS_COLORS.expired
        const days = getDaysRemaining(sub.expires_at)
        return (
          <Stack spacing={0.25}>
            <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
              <Chip label={sub.status} size="small" sx={{
                bgcolor: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                fontSize: 10, fontWeight: 700, textTransform: 'uppercase', height: 20,
              }} />
              <Typography variant="caption" color="#475569" fontWeight={600}>
                {sub.device_count} devices
              </Typography>
            </Box>
            <Typography variant="caption" color="#94a3b8" sx={{ fontSize: 10 }}>
              ${sub.total_amount.toFixed(0)}/{sub.billing_cycle === 'yearly' ? 'yr' : 'mo'}
              {sub.status !== 'expired' && ` · ${days}d left`}
            </Typography>
          </Stack>
        )
      },
    },
    {
      field: 'currency', headerName: 'Currency', width: 80,
      renderCell: ({ value }) => (
        <Chip label={value || 'USD'} size="small" sx={{
          bgcolor: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0',
          fontSize: 11, fontWeight: 600,
        }} />
      ),
    },
    {
      field: 'status', headerName: 'Status', width: 110,
      renderCell: ({ value }) => {
        const cfg = STATUS_COLORS[value] ?? STATUS_COLORS.unknown
        return (
          <Chip label={value} size="small" sx={{
            bgcolor: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            fontSize: 11, fontWeight: 600,
          }} />
        )
      },
    },
    {
      field: 'created_at', headerName: 'Created', width: 120,
      renderCell: ({ value }) => (
        <Typography variant="caption" color="#64748b">
          {value ? format(new Date(value), 'MMM d, yyyy') : '\u2014'}
        </Typography>
      ),
    },
    {
      field: 'actions', headerName: '', width: 180, sortable: false, align: 'right',
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Subscribe / Renew">
            <IconButton size="small" onClick={() => openSubscribe(row.id)}
              sx={{ color: '#94a3b8', '&:hover': { color: '#34A8E0', bgcolor: '#EBF6FD' } }}>
              <Subscriptions sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => openEdit(row)}
              sx={{ color: '#94a3b8', '&:hover': { color: '#34A8E0', bgcolor: '#EBF6FD' } }}>
              <Edit sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {row.status === 'suspended' ? (
            <Tooltip title="Activate tenant">
              <IconButton size="small" onClick={() => activateTenant.mutate(row.id)}
                sx={{ color: '#94a3b8', '&:hover': { color: '#10b981', bgcolor: '#f0fdf4' } }}>
                <CheckCircle sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Suspend tenant">
              <IconButton size="small" onClick={() => setSuspendTarget(row.id)}
                sx={{ color: '#94a3b8', '&:hover': { color: '#f59e0b', bgcolor: '#fffbeb' } }}>
                <Block sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete tenant">
            <IconButton size="small" onClick={() => setDeleteTarget(row.id)}
              sx={{ color: '#94a3b8', '&:hover': { color: '#ef4444', bgcolor: '#fff1f2' } }}>
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <Box>
      <AdminPageHeader
        eyebrow="Organizations"
        title="Tenant Management"
        description={`${tenants.length} tenants · ${activeCt} active · Manage access, subscriptions, and tenant ownership.`}
        icon={<Business />}
        actions={<Button variant="contained" startIcon={<Add />} onClick={openCreate}>New Tenant</Button>}
      />

      {(createTenant.isError || updateTenant.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>Failed to save tenant</Alert>
      )}
      {(tenantsQuery.isError || subscriptionsQuery.isError || tiersQuery.isError) && (
        <AdminDataError
          message="Some tenant data could not be loaded. Subscription details may be incomplete."
          onRetry={() => {
            void tenantsQuery.refetch()
            void subscriptionsQuery.refetch()
            void tiersQuery.refetch()
          }}
        />
      )}

      <Card sx={{ bgcolor: '#ffffff', overflow: 'hidden' }}>
        <Box sx={{
          p: 1.5, display: 'flex', alignItems: { xs: 'stretch', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' }, gap: 1.25, borderBottom: '1px solid', borderColor: 'divider',
        }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tenant, ID, or currency…"
            aria-label="Search tenants"
            sx={{ width: { xs: '100%', sm: 320 } }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 19, color: 'text.secondary' }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear tenant search" onClick={() => setSearch('')}><Close sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto' }}>
            {(['all', 'active', 'suspended'] as const).map((status) => (
              <Chip
                key={status}
                clickable
                label={status === 'all' ? `All ${tenants.length}` : `${status[0].toUpperCase()}${status.slice(1)} ${tenants.filter((t: Tenant) => t.status === status).length}`}
                onClick={() => setStatusFilter(status)}
                color={statusFilter === status ? 'primary' : 'default'}
                variant={statusFilter === status ? 'filled' : 'outlined'}
              />
            ))}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ ml: { sm: 'auto' }, whiteSpace: 'nowrap' }}>
            {filteredTenants.length} shown
          </Typography>
        </Box>
        <DataGrid
          rows={filteredTenants}
          columns={columns}
          loading={isLoading}
          autoHeight
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          disableRowSelectionOnClick
          sx={adminGridSx}
          slots={{
            noRowsOverlay: () => (
              <AdminEmptyState
                compact
                icon={<Business />}
                title={tenants.length === 0 ? 'No tenants yet' : 'No matching tenants'}
                description={tenants.length === 0 ? 'Create the first tenant and its initial administrator.' : 'Try a different search term or clear the active filter.'}
                actionLabel={tenants.length === 0 ? 'Create tenant' : 'Clear filters'}
                onAction={tenants.length === 0 ? openCreate : () => { setSearch(''); setStatusFilter('all') }}
              />
            ),
          }}
        />
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ pb: 1, fontWeight: 700, color: '#0f172a' }}>
          {editing ? 'Edit Tenant' : 'Create New Tenant'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField label="Tenant Name" value={form.name} required fullWidth autoFocus size="small"
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
          {!editing && (
            <TextField select label="Currency" value={form.currency} fullWidth size="small"
              helperText="Tenant will see prices in this currency"
              onChange={(e) => setForm((p) => ({ ...p, currency: e.target.value }))}>
              {CURRENCIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
          )}

          {!editing && (
            <>
              <Box sx={{ mt: 1, pt: 1.5, borderTop: '1px solid #f1f5f9' }}>
                <Typography variant="caption" fontWeight={700} color="#64748b" sx={{ letterSpacing: '0.05em' }}>
                  INITIAL TENANT ADMIN
                </Typography>
                <Typography variant="caption" color="#94a3b8" display="block" sx={{ mt: 0.25 }}>
                  This account will be created with the <b>admin</b> role and full permissions.
                </Typography>
              </Box>
              <TextField label="Admin Name" value={form.admin_name} fullWidth size="small"
                onChange={(e) => setForm((p) => ({ ...p, admin_name: e.target.value }))} />
              <TextField label="Admin Email" type="email" value={form.admin_email} required fullWidth size="small"
                onChange={(e) => setForm((p) => ({ ...p, admin_email: e.target.value }))} />
              <TextField label="Admin Password" type="password" value={form.admin_password} required fullWidth size="small"
                autoComplete="new-password"
                error={form.admin_password !== '' && !isPasswordValid(form.admin_password)}
                helperText={
                  (form.admin_password !== '' && passwordError(form.admin_password)) ||
                  'At least 10 characters, mixing upper, lower, digits or symbols'
                }
                onChange={(e) => setForm((p) => ({ ...p, admin_password: e.target.value }))} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}
            disabled={
              createTenant.isPending || updateTenant.isPending || bootstrapAdmin.isPending ||
              !form.name ||
              (!editing && (!form.admin_email || !isPasswordValid(form.admin_password)))
            }
            sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
            {editing ? 'Update' : 'Create Tenant + Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Subscribe Tenant Dialog */}
      <Dialog open={subDialogOpen} onClose={() => setSubDialogOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Subscribe Tenant
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info" sx={{ fontSize: 12 }}>
            Subscribe this tenant directly (e.g. cash payment received offline). The subscription will be activated immediately.
          </Alert>
          {subTenantId && (
            <Typography variant="body2" color="#475569">
              Tenant: <b>{(tenants as Tenant[]).find((t) => t.id === subTenantId)?.name ?? subTenantId.slice(0, 8)}</b>
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Device Count" type="number" value={subForm.device_count} required size="small" sx={{ flex: 1 }}
              inputProps={{ min: 1 }}
              onChange={(e) => setSubForm((p) => ({ ...p, device_count: e.target.value }))} />
            <TextField select label="Billing Cycle" value={subForm.billing_cycle} required size="small" sx={{ flex: 1 }}
              onChange={(e) => setSubForm((p) => ({ ...p, billing_cycle: e.target.value }))}>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Box>
          {tiers.length > 0 && (
            <Alert severity="success" icon={false} sx={{ fontSize: 11, py: 0.5 }}>
              Pricing tiers: {tiers.map((t) => `${t.name} (${t.min_devices}–${t.max_devices ?? '\u221E'} @ $${t.price_per_device_monthly}/dev/mo)`).join(' · ')}
            </Alert>
          )}
          <TextField label="Amount Override (optional)" type="number" value={subForm.amount} size="small" fullWidth
            inputProps={{ min: 0, step: '0.01' }}
            helperText="Leave empty to calculate from pricing tiers. Overrides must be greater than zero."
            onChange={(e) => setSubForm((p) => ({ ...p, amount: e.target.value }))} />
          <TextField label="Description / Notes" value={subForm.description} size="small" fullWidth
            placeholder="e.g. Cash payment, Invoice #1234"
            onChange={(e) => setSubForm((p) => ({ ...p, description: e.target.value }))} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setSubDialogOpen(false)} sx={{ color: '#64748b' }}>Cancel</Button>
          <Button variant="contained"
            disabled={
              subscribeTenant.isPending ||
              !subForm.device_count ||
              Number(subForm.device_count) < 1 ||
              (subForm.amount !== '' && Number(subForm.amount) <= 0)
            }
            onClick={handleSubscribe}
            sx={{ background: 'linear-gradient(135deg,#34A8E0,#2B8BB9)', borderRadius: 1.5, fontWeight: 600 }}>
            {subscribeTenant.isPending ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!suspendTarget}
        title="Suspend Tenant"
        message="This immediately blocks new sign-ins and API access for this tenant. You can reactivate it later."
        confirmLabel="Suspend"
        confirmColor="warning"
        onConfirm={async () => {
          try {
            await suspendTenant.mutateAsync(suspendTarget!)
            setSuspendTarget(null)
          } catch { /* API interceptor shows the failure */ }
        }}
        onClose={() => setSuspendTarget(null)}
        loading={suspendTenant.isPending}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Tenant"
        message="This will permanently delete the tenant and all its data. This cannot be undone."
        onConfirm={async () => {
          try {
            await deleteTenant.mutateAsync(deleteTarget!)
            setDeleteTarget(null)
          } catch { /* API interceptor shows the failure */ }
        }}
        onClose={() => setDeleteTarget(null)}
        loading={deleteTenant.isPending}
      />

      <Dialog open={!!createdInfo} onClose={() => setCreatedInfo(null)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 2.5, border: '1px solid #e2e8f0' } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a', pb: 1 }}>
          Tenant Created
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>
            Tenant <b>{createdInfo?.tenant}</b> is ready. Use the Subscribe button to activate their subscription.
          </Alert>
          <Typography variant="body2" color="#475569" sx={{ mb: 1 }}>
            Hand these credentials to the tenant administrator:
          </Typography>
          <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1.5, fontFamily: 'monospace', fontSize: 13 }}>
            <Box>URL: <b>{TENANT_LOGIN_URL}</b></Box>
            <Box>Email: <b>{createdInfo?.email}</b></Box>
            <Box>Password: (the one you just set)</Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="contained" onClick={() => setCreatedInfo(null)}
            sx={{ background: 'linear-gradient(135deg, #34A8E0, #2B8BB9)', borderRadius: 1.5 }}>
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
